/* eslint jsx-a11y/aria-props: "off" */ // all browsers support ara-description https://caniuse.com/?search=aria-description && tested in jaws
import { messageService } from '../../../Service/message-service';
import React, { useMemo } from 'react';
import { Link } from '@qualtrics/ui-react';
import PropTypes from 'prop-types';
import { createDateTimeFormatter } from '@qualtrics/i18n-lib/lib/date-time-formatters/xm-admin';
import { useUserInfo } from '../../../Context';
import { DateStyle } from '@qualtrics/i18n-lib/lib/model';

import styles from './2FAInfo.scss';

export const TwoFAInfo = ({
  additionalAttributes,
  descriptionTranslationId,
  linkTranslationId,
  linkHref,
  openInTheNewTab,
}) => {
  const { language } = useUserInfo();
  const linkPlaceholder = '[linkPlaceholder]';
  const dateFormatter = useMemo(
    () => createDateTimeFormatter(language, 'UTC'),
    [language],
  );

  const evaluatedLinkTranslationId = useMemo(() => {
    if (additionalAttributes?.[1] === 'true') {
      return linkTranslationId.extension || linkTranslationId.base;
    }
    return linkTranslationId.base;
  }, [additionalAttributes, linkTranslationId]);

  const linkBanner = messageService.get(evaluatedLinkTranslationId);
  const opensInANewTabMessage = messageService.get('OPENS_IN_NEW_TAB_WARNING');
  const link = useMemo(
    () => (
      <Link
        href={linkHref}
        target={openInTheNewTab ? '_blank' : undefined}
        className={styles.link}
        aria-description={openInTheNewTab ? opensInANewTabMessage : undefined}
      >
        {linkBanner}
      </Link>
    ),
    [linkBanner, linkHref, openInTheNewTab, opensInANewTabMessage],
  );

  const date = useMemo(() => {
    if (!additionalAttributes || additionalAttributes.length === 0) {
      return undefined;
    }

    const dateAsTimestamp = parseInt(additionalAttributes[0], 10);

    if (!dateAsTimestamp || isNaN(dateAsTimestamp)) {
      return undefined;
    }

    return dateFormatter(new Date(dateAsTimestamp * 1000), DateStyle.Long); // go returns value in seconds
  }, [additionalAttributes, dateFormatter]);

  const translatedDescriptionWithDate = useMemo(
    () =>
      date &&
      messageService
        .get(descriptionTranslationId, date, linkPlaceholder)
        .split(linkPlaceholder),
    [date, descriptionTranslationId],
  );

  return translatedDescriptionWithDate ? (
    <>
      {translatedDescriptionWithDate[0]}
      {link}
      {translatedDescriptionWithDate.slice(1)}
    </>
  ) : null;
};

TwoFAInfo.propTypes = {
  additionalAttributes: PropTypes.arrayOf(PropTypes.string).isRequired,
  descriptionTranslationId: PropTypes.string.isRequired,
  linkTranslationId: PropTypes.shape({
    base: PropTypes.string.isRequired,
    extension: PropTypes.string,
  }).isRequired,
  linkHref: PropTypes.string.isRequired,
  openInTheNewTab: PropTypes.bool,
};
